import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useInfiniteQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import NumericInput from "src/main/PointOfSale/CurrentSalePanel/NumericInput"

import Button from "src/components/Button"
import Form from "src/components/Form"

import { getServiceProducts } from "src/api/Service/Products"

import useDebounce from "src/hooks/use_debounce"
import { useTracker } from "src/hooks/use_tracker"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { capitalize } from "src/utils/string_helpers"

import { computeSubtotal } from "./helpers"

const PER_PAGE = 25

const pricePrecisionDigits = (pricePrecision) =>
  pricePrecision === "hundredths_of_cents" ? 4 : 2

const getQuantityLabel = (product) => {
  if (!product?.quantityUnitLabel || product.quantityUnitLabel === "ft")
    return "Qty"
  return capitalize(product.quantityUnitLabel)
}

const isFixedByLength = (product) => product?.pricingStructure === "by_length"

const JobLineItemForm = ({
  availability,
  boatLengthFeet,
  initialValues,
  onSubmit,
  onCancel,
  submitLabel = "Save",
  lockProduct = false,
  initialFocus = "product",
  onDirtyChange,
  templateRendering = false,
}) => {
  const { currencySymbol, marinaSlug, presentmentCurrency } = useMarina()
  const tracker = useTracker()

  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSetSearchQuery] = useDebounce(setSearchQuery)
  const [selectedProduct, setSelectedProduct] = useState(
    initialValues?.product ?? null
  )

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      product_id: initialValues?.productId ?? "",
      price_per_unit: initialValues?.pricePerUnit ?? 0,
      quantity:
        initialValues?.quantity != null ? Number(initialValues.quantity) : 1,
      notes: initialValues?.notes ?? "",
    },
  })

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    if (initialFocus === "product") return undefined

    const targetId =
      initialFocus === "rate" ? "line-item-rate" : "line-item-notes"
    // Defer past Modal's own initialFocus, which fires after mount.
    const handle = setTimeout(() => {
      document.getElementById(targetId)?.focus()
    }, 0)
    return () => clearTimeout(handle)
  }, [initialFocus])

  const pricePerUnit = watch("price_per_unit")
  const quantity = watch("quantity")

  const ratePrecision = pricePrecisionDigits(selectedProduct?.pricePrecision)
  const rateMultiplier = 10 ** ratePrecision

  const subtotal = useMemo(() => {
    return computeSubtotal({
      pricePerUnit,
      quantity,
      pricePrecision: selectedProduct?.pricePrecision,
    })
  }, [pricePerUnit, quantity, selectedProduct])

  const {
    data: productsData,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ["serviceProducts", marinaSlug, availability, searchQuery],
    ({ pageParam = 1 }) =>
      getServiceProducts(marinaSlug, {
        availability,
        q: searchQuery,
        page: pageParam,
        perPage: PER_PAGE,
      }).then((page) => {
        if (pageParam === 1) {
          tracker.trackEvent("work_orders:add_line_item:product_search", {
            query: searchQuery,
            queryLength: searchQuery.length,
            resultCount: page.length,
            availability,
          })
        }
        return page
      }),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === PER_PAGE ? allPages.length + 1 : undefined,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  )

  const products = useMemo(
    () => productsData?.pages.flat() ?? [],
    [productsData]
  )

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleInputValueChange = useCallback(
    (nextValue, eventDetails) => {
      const reason = eventDetails?.reason
      const userTyped = reason === "input-change" || reason === "input-paste"
      const userCleared =
        reason === "input-clear" || (userTyped && nextValue === "")

      if (userTyped) {
        debouncedSetSearchQuery(nextValue)
      }

      if (userCleared && selectedProduct) {
        setSelectedProduct(null)
        setValue("product_id", "")
        setValue("price_per_unit", 0)
        setValue("quantity", 0)
      }
    },
    [debouncedSetSearchQuery, selectedProduct, setValue]
  )

  const handleProductSelect = useCallback(
    (product) => {
      if (!product) return

      tracker.trackEvent("work_orders:add_line_item:product_selected", {
        query: searchQuery,
        queryLength: searchQuery.length,
        productId: product.id,
        productName: product.name,
        resultPosition: products.indexOf(product),
        resultCount: products.length,
        availability,
      })

      setSelectedProduct(product)
      setValue("product_id", product.id)
      setValue("price_per_unit", product.pricePerUnit ?? 0)

      if (product.pricingStructure === "by_length" && templateRendering) {
        setValue("quantity", 0)
      } else if (product.pricingStructure === "by_length" && boatLengthFeet) {
        setValue("quantity", boatLengthFeet)
      } else {
        setValue("quantity", 1)
      }
    },
    [
      setValue,
      boatLengthFeet,
      tracker,
      searchQuery,
      products,
      availability,
      templateRendering,
    ]
  )

  const submit = (values) => {
    if (templateRendering) {
      onSubmit({
        productId: values.product_id,
        quantity: values.quantity,
        product: selectedProduct,
      })
    } else {
      onSubmit({
        productId: values.product_id,
        pricePerUnit: values.price_per_unit,
        quantity: values.quantity,
        notes: values.notes ?? "",
        product: selectedProduct,
      })
    }
  }

  // Combobox uses `items` to resolve the selected value to a label via
  // itemToStringLabel. In edit mode, initialValues.product may not be in the
  // fetched page, so seed it into the list to keep the input label rendering.
  const itemsForCombobox = useMemo(() => {
    if (
      initialValues?.product &&
      !products.some((p) => p.id === initialValues.product.id)
    ) {
      return [initialValues.product, ...products]
    }
    return products
  }, [products, initialValues?.product])

  const byLength = isFixedByLength(selectedProduct)

  const getSubtotalDisplay = () => {
    if (templateRendering && byLength) {
      return "—"
    } else {
      return formatCurrencyFromMinorUnits({
        amount: subtotal,
        currency: presentmentCurrency,
        precision: 2,
      })
    }
  }

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <div className="flex flex-col gap-4">
        <div>
          <Form.Label htmlFor="line-item-product" required>
            {availability === "service_labor" ? "Labor item" : "Parts item"}
          </Form.Label>
          <Controller
            name="product_id"
            control={control}
            rules={{ required: "Please select an item" }}
            render={() => (
              <Form.Combobox
                id="line-item-product"
                autoFocus={initialFocus === "product"}
                disabled={lockProduct}
                items={itemsForCombobox}
                filteredItems={itemsForCombobox}
                value={selectedProduct}
                onValueChange={handleProductSelect}
                onInputValueChange={handleInputValueChange}
                onLoadMore={handleLoadMore}
                itemToStringLabel={(item) => item?.name ?? ""}
                isItemEqualToValue={(item, val) => item?.id === val?.id}
                isLoading={isFetching}
                placeholder="Search items..."
                hasErrors={!!errors.product_id}
                noResultsContent={isFetching ? "Loading…" : "No items found"}
                renderOption={({ option }) => (
                  <div className="flex flex-col py-0.5">
                    <span className="font-semibold text-gray-900">
                      {option.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {option.pricePerUnit != null
                        ? formatCurrencyFromMinorUnits({
                            amount: option.pricePerUnit,
                            currency: presentmentCurrency,
                            precision: pricePrecisionDigits(
                              option.pricePrecision
                            ),
                          })
                        : "—"}
                      {option.rateUnitLabel}
                    </span>
                  </div>
                )}
              />
            )}
          />
          {errors.product_id && (
            <Form.Error>{errors.product_id.message}</Form.Error>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Form.Label htmlFor="line-item-rate" required>
              Rate
            </Form.Label>
            <Controller
              name="price_per_unit"
              control={control}
              rules={{
                validate: (v) => v > 0 || "Rate must be greater than 0",
              }}
              render={({ field }) => (
                <NumericInput
                  key={`rate-${selectedProduct?.id}`}
                  id="line-item-rate"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  precision={ratePrecision}
                  valueMultiplier={rateMultiplier}
                  icon={currencySymbol}
                  iconPosition="left"
                  disabled={!selectedProduct || !!templateRendering}
                  hasErrors={!!errors.price_per_unit}
                  minValue={0}
                />
              )}
            />
          </div>

          <span className="mt-8 text-sm text-gray-400">&times;</span>

          <div className="flex-1">
            <Form.Label htmlFor="line-item-quantity" required>
              {getQuantityLabel(selectedProduct)}
            </Form.Label>
            {templateRendering && byLength ? (
              <div
                id="line-item-quantity"
                className="flex h-10 items-center rounded border border-gray-300 bg-gray-100 px-3 text-sm text-gray-500"
              >
                <span className="italic">{`auto · by ${selectedProduct.quantityUnitLabel}`}</span>
              </div>
            ) : (
              <Controller
                name="quantity"
                control={control}
                rules={{
                  validate: (v) =>
                    templateRendering && byLength
                      ? true
                      : v > 0 || "Quantity must be greater than 0",
                }}
                render={({ field }) => (
                  <NumericInput
                    key={`qty-${selectedProduct?.id}`}
                    id="line-item-quantity"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    precision={2}
                    disabled={!selectedProduct}
                    hasErrors={!!errors.quantity}
                    minValue={0}
                  />
                )}
              />
            )}
          </div>

          <span className="mt-8 text-sm text-gray-400">=</span>

          <div className="flex-1">
            <Form.Label>Subtotal</Form.Label>
            <div
              className={classNames(
                "flex h-10 items-center rounded border px-3 text-sm font-semibold",
                {
                  "bg-gray-100": templateRendering,
                  "border-green-200 bg-green-50 text-green-800":
                    !templateRendering,
                }
              )}
              data-testid="line-item-subtotal"
            >
              {getSubtotalDisplay()}
            </div>
          </div>
        </div>
        <div>
          {!templateRendering && errors.price_per_unit && (
            <Form.Error>{errors.price_per_unit.message}</Form.Error>
          )}
          {templateRendering && errors.price_per_unit && (
            <Form.Error>
              Item default price can not be $0.00 - update{" "}
              <a
                className="font-semibold"
                href={`/manage/${marinaSlug}/items/${selectedProduct.id}/edit`}
              >
                {"here"}
              </a>
              .
            </Form.Error>
          )}
          {errors.quantity && (
            <Form.Error>{errors.quantity.message}</Form.Error>
          )}
          {templateRendering && byLength && (
            <p className="text-sm text-gray-500">
              Quantity for this line item will be automatically calculated when
              this template is applied to a boat on a work order.
            </p>
          )}
        </div>
        {!templateRendering && (
          <div>
            <Form.Label htmlFor="line-item-notes">Note</Form.Label>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Form.Textarea
                  id="line-item-notes"
                  rows={2}
                  placeholder="Additional information (shows on invoice)"
                  disabled={!selectedProduct && !lockProduct}
                  maxLength={300}
                  {...field}
                />
              )}
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={lockProduct ? !isDirty : !selectedProduct}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </Form>
  )
}

JobLineItemForm.propTypes = {
  availability: PropTypes.oneOf(["service_labor", "service_parts"]).isRequired,
  boatLengthFeet: PropTypes.number,
  initialValues: PropTypes.shape({
    product: PropTypes.object,
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pricePerUnit: PropTypes.number,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    notes: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  submitLabel: PropTypes.string,
  lockProduct: PropTypes.bool,
  initialFocus: PropTypes.oneOf(["product", "rate", "notes"]),
  onDirtyChange: PropTypes.func,
  templateRendering: PropTypes.bool,
}

export default JobLineItemForm
